import React from 'react';
import Icon from '@mdi/react';
import { mdiCashMultiple, mdiLinkPlus, mdiPencilOutline, mdiWalletOutline } from '@mdi/js';
import _ from 'lodash';
import { Tooltip, Variables } from '@enter/core';
import { formatDate } from '../../../services/primitives/date-utils';
import { formatMoney } from '../utils/statement-format.utils';
import { StatementDataTableColumn } from './statement-data-table';
import type { IStatementData, PaymentsTableRow } from '../statement-details.types';
import * as S from '../statement-details.styles';
import {
    StatementAmount,
    StatementMutedText as StatementMuted,
    StatementSecondaryText as StatementSecondary,
    StatementTypeWithIcon,
} from './statement-ui';

export type CreatePaymentsTableColumnsArgs = {
    showActions: boolean;
    onEditPayment: (row: IStatementData) => void | Promise<void>;
    onAllocatePayment: (row: IStatementData) => void | Promise<void>;
};

export const createPaymentsTableColumns = ({
    showActions,
    onEditPayment,
    onAllocatePayment,
}: CreatePaymentsTableColumnsArgs): StatementDataTableColumn<PaymentsTableRow>[] =>
    _.compact([
        {
            id: 'date',
            header: 'Date',
            width: 120,
            render: row => (
                <StatementMuted>{row.date ? formatDate(row.date, 'MMM dd, yyyy') : '—'}</StatementMuted>
            ),
        },
        {
            id: 'type',
            header: 'Type',
            width: 220,
            render: row => (
                <>
                    <StatementTypeWithIcon>
                        <Icon
                            path={row.tone === 'warning' ? mdiCashMultiple : mdiWalletOutline}
                            size={0.8}
                            color={row.tone === 'warning' ? Variables.Colors.sunset500 : Variables.Colors.g200}
                        />
                        {row.type}
                    </StatementTypeWithIcon>
                    {row.ref && <StatementSecondary>{row.ref}</StatementSecondary>}
                </>
            ),
        },
        {
            id: 'applied',
            header: 'Applied to',
            width: 200,
            render: row => <StatementMuted>{row.applied}</StatementMuted>,
        },
        {
            id: 'amount',
            header: 'Amount',
            width: 120,
            render: row => <StatementAmount tone={row.tone}>{formatMoney(row.amount)}</StatementAmount>,
        },
        {
            id: 'allocated',
            header: 'Allocated',
            width: 120,
            render: row => <StatementMuted>{formatMoney(row.allocatedAmount)}</StatementMuted>,
        },
        {
            id: 'unallocated',
            header: 'Unallocated',
            width: 120,
            render: row => <StatementMuted>{formatMoney(row.unallocatedAmount)}</StatementMuted>,
        },
        showActions
            ? {
                  id: 'actions',
                  header: 'Actions',
                  width: 88,
                  align: 'right' as const,
                  render: row => {
                      if (!row.action || !row.actionRow) {
                          return null;
                      }
                      const isAllocate = row.action === 'allocate';
                      const label = isAllocate ? 'Allocate' : 'Edit';
                      return (
                          <S.PaymentActions>
                              <Tooltip direction="top" title={<span>{label}</span>}>
                                  <span>
                                      <S.PaymentActionButton
                                          type="button"
                                          variant="unstyled"
                                          aria-label={label}
                                          onClick={event => {
                                              event.stopPropagation();
                                              const handler = isAllocate ? onAllocatePayment : onEditPayment;
                                              const result = handler(row.actionRow!);
                                              if (result instanceof Promise) {
                                                  result.catch(err => {
                                                      console.error('Error in payment row action:', err);
                                                  });
                                              }
                                          }}>
                                          <Icon path={isAllocate ? mdiLinkPlus : mdiPencilOutline} size={0.75} />
                                      </S.PaymentActionButton>
                                  </span>
                              </Tooltip>
                          </S.PaymentActions>
                      );
                  },
              }
            : undefined,
    ]);
